import React from 'react'
import { TFunction } from 'i18next'
import moment from 'moment'
import { Empty } from 'src/components/atoms'
import { R14Data, R14Header } from 'src/store/ducks/reports/types'
import { Title } from 'src/components/Ant'
import { TableItems, Total } from './block-list'
import { Spacer } from './styles'
import SectionHeader from './section-header'
import { ControlReportRefProps, Page, PageItem } from '../types'
import TitleReport from '../../title-report'
import { RequestTicketStatus, RequestUserDataStatus } from 'src/types'

const mesures = {
  REPORT_TITLE: 55.5,
  REPORT_HEADER: 80,
  LIST_HEADER: 38,
  LIST_TITLE: 30,
  MINIMUM_BLOCK: 92,
  BLOCK_TOTAL: 30,
  PAGE_HEIGHT: 691.68,
  REGISTER_SINGLE: 24,
  SPACER: 16
}

export function getCvsHeader(header: R14Header[], t: TFunction) {
  const { ended_on, started_on } = header?.[0] || {}
  return `${t('web.report.12.header.start_date')},${
    moment(moment.unix(started_on).utc().format('YYYYMMDD')).format(
      'DD/MM/YYYY'
    ) || '-'
  }\n${t('web.report.12.header.end_date')},${
    moment(moment.unix(ended_on).utc().format('YYYYMMDD')).format(
      'DD/MM/YYYY'
    ) || '-'
  }\n\n${t('common.ticket.type.expense')},${t(
    'web.report.12.data.request-number'
  )},${t('web.report.12.data.date')},${t(
    'web.report.12.header.participant_name'
  )},${t('web.setup.form.field-title.title')},${t(
    'web.receipt.form.service.title'
  )},${t('web.resources.cost-centers.title')},${t(
    'web.resources.reasons.title'
  )},${t('web.report.10.data.ticket_status')},${t(
    'web.tree.flows.conferences.title'
  )},${t('web.report.12.data.total')},${t(
    'web.receipt.form.justification.title'
  )}\n`
}

function translateTicketStatus(
  status: RequestTicketStatus | RequestUserDataStatus
) {
  switch (status) {
    case 'UNDERWAY':
      return 'Em andamento'
    case 'APPROVAL_PENDING':
      return 'Aprovação pendente'
    case 'APPROVAL_REPROVED':
      return 'Aprovação reprovada'
    case 'CANCELED':
      return 'Cancelado'
    case 'CLOSING_PENDING':
      return 'Fechamento pendente'
    case 'CLOSING_REPROVED':
      return 'Fechamento reprovado'
    case 'CONFERENCE_PENDING':
      return 'Conferência pendente'
    case 'CONFERENCE_REPROVED':
      return 'Conferência reprovada'
    case 'EMISSION_CANCELED':
      return 'Emissão cancelada'
    case 'EMISSION_PENDING':
      return 'Emissão pendente'
    case 'FINISHED':
      return 'Finalizado'
    case 'ISSUED':
      return 'Emitido'
    case 'QUOTATION_PENDING':
      return 'Cotação pendente'
    default:
      return status
  }
}

export function generateCvsReduce(acc: string, cur: R14Data) {
  return acc.concat(
    `${cur.request_ticket_receipt_number ?? ''},${cur.request_number},${
      moment(moment.unix(cur.datetrunc).utc().format('YYYYMMDD')).format(
        'DD/MM/YYYY'
      ) || '-'
    },"${cur.traveler}","${cur.title}","${cur.service}","${cur.cost_center}","${
      cur.reason
    }","${translateTicketStatus(cur.status)}",${
      Boolean(cur.checked_on)
        ? moment(moment.unix(cur.checked_on).utc().format('YYYYMMDD')).format(
            'DD/MM/YYYY'
          )
        : '-'
    },${cur.total?.toFixed(2) ?? ''},"${cur.justification || ''}"\n`
  )
}

export function thereIsSpaceForMore(
  ref: React.MutableRefObject<ControlReportRefProps>,
  data: R14Data[]
): boolean {
  if (ref.current.reportIndex >= data.length) {
    return false
  }

  const nextFieldHeight = mesures.MINIMUM_BLOCK

  return nextFieldHeight + mesures.SPACER <= ref.current.availableSpace
}

export function getRangedBlocks(
  ref: React.MutableRefObject<ControlReportRefProps>,
  data: R14Data[]
): number {
  let indexStart = ref.current.reportIndex
  const activeUserName = data[ref.current.reportIndex].traveler

  do {
    if (
      activeUserName !== data[indexStart].traveler ||
      ref.current.availableSpace - mesures.REGISTER_SINGLE < 0
    ) {
      return indexStart
    }

    ref.current.availableSpace -= mesures.REGISTER_SINGLE
    indexStart++
  } while (indexStart < data.length)

  return indexStart
}

export function generateReportPages(
  ref: React.MutableRefObject<ControlReportRefProps>,
  data: R14Data[],
  header: R14Header[]
): Page[] {
  const pages: Page[] = []

  if (!data.length) {
    return [
      {
        index: 0,
        content: [
          {
            index: 1,
            content: (
              <>
                <TitleReport title="web.report.14.title" />
                <SectionHeader data={header} />
                <Empty />
              </>
            )
          }
        ]
      }
    ]
  }

  do {
    const page: PageItem[] = []
    ref.current.availableSpace = mesures.PAGE_HEIGHT
    let needPutTotal = false
    page.push({
      index: ref.current.nextIndex++,
      content: <TitleReport title="web.report.14.title" />
    })
    ref.current.availableSpace -= mesures.REPORT_TITLE

    if (ref.current.reportIndex === 0) {
      page.push({
        index: ref.current.nextIndex++,
        content: <SectionHeader data={header} />
      })
      ref.current.availableSpace -= mesures.REPORT_HEADER
    }

    do {
      page.push({
        index: ref.current.nextIndex++,
        content: (
          <Title level={4}>{data[ref.current.reportIndex].traveler}</Title>
        )
      })
      ref.current.availableSpace -= mesures.LIST_TITLE
      ref.current.availableSpace -= mesures.LIST_HEADER

      if (needPutTotal) {
        const total = data
          .filter(
            cur => cur.traveler === data[ref.current.reportIndex - 1].traveler
          )
          .reduce((acc, { total }) => acc + total, 0)
        page.push({
          index: ref.current.nextIndex++,
          content: <Total value={total} />
        })
        ref.current.availableSpace -= mesures.BLOCK_TOTAL
        needPutTotal = false
      }

      const rangedBlocks = getRangedBlocks(ref, data)

      page.push({
        index: ref.current.nextIndex++,
        content: (
          <TableItems
            items={data.slice(ref.current.reportIndex, rangedBlocks)}
          />
        )
      })

      ref.current.reportIndex = rangedBlocks

      if (
        data[ref.current.reportIndex - 1].traveler !==
        data?.[ref.current.reportIndex]?.traveler
      ) {
        if (ref.current.availableSpace < mesures.BLOCK_TOTAL) {
          needPutTotal = true
        } else {
          const total = data
            .filter(
              cur => cur.traveler === data[ref.current.reportIndex - 1].traveler
            )
            .reduce((acc, { total }) => acc + total, 0)
          page.push({
            index: ref.current.nextIndex++,
            content: <Total value={total} />
          })
          ref.current.availableSpace -= mesures.BLOCK_TOTAL
        }
      }

      if (thereIsSpaceForMore(ref, data)) {
        page.push({
          index: ref.current.nextIndex++,
          content: <Spacer />
        })
        ref.current.availableSpace -= mesures.SPACER
      } else {
        break
      }
    } while (ref.current.reportIndex < data.length)
    pages.push({
      index: pages.length,
      content: page
    })
  } while (ref.current.reportIndex < data.length)

  return pages
}

export function handleDownloadCsv(csvString: BlobPart, t: TFunction) {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${t('web.report.14.title')}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
